import { defaultPropertyDefinitions, STANDARD_SYMBOL_MAPPING, STANDARD_SYMBOL_PIN_MAPPING, } from "@buildwithflux/constants";
import { VectorObjectValue2, VectorObjectValue3, } from "@buildwithflux/models";
import { groupByWithPreservedOrder, keySort } from "@buildwithflux/shared";
import { mapValues } from "lodash";
import { box2, vec2 } from "../common/geometry";
import { getPropertyValue } from "../property";
import { CustomSymbolLayout } from "./CustomSymbolLayout";
import { DEFAULT_SYMBOL_NAME, ElementHelper } from "./ElementHelper";
export const SymbolConstants = {
    // TODO original pinGridSize choice was 8px (per Brooks' design) but that left in-group pins too close together.
    // 10px is a demo-only compromise that at least leaves the pin labels closer to the right pin than the one above it.
    // Post-demo, we'll want to hone the design for this.
    pinGridSize: 10,
    moduleOutlineWidth: 8 * 19,
    // Legacy modules:
    legacyPinLength: 5,
    pin_margin_top: 5,
    pin_margin_bottom: 5,
    pin_to_pin_margin: 10,
    min_object_width: 60,
    min_object_height: 30,
};
// A cache intended to only be used as a wrapper around tight loops, and reset every time.
// We do this because the cache value isn't a pure function of its key, so we don't want
// to introduce a bug-prone cache just to solve tight-loop perf.
let parametricInfoByPartUidCache = undefined;
export class SymbolLayout {
    /** Temporarily create a cache of parametric symbol info, to execute a block of code that
     * heavily depends on it, before clearing the cache. This is useful for tight loops at a
     * higher level of abstraction that don't directly call getSymbolInfo, but call
     * other functions that do, deeper in the callstack. It's a safe alternative to global
     * cache without having to drill extra parameters deep into a callstack.
     */
    static withParametricPartVersionCache(elements, callback) {
        parametricInfoByPartUidCache = {};
        for (const element of elements) {
            if (element.part_uid in parametricInfoByPartUidCache)
                continue;
            parametricInfoByPartUidCache[element.part_uid] = this.getSymbolInfo(element);
        }
        callback();
        parametricInfoByPartUidCache = undefined;
    }
    /** Calculate info relevant to laying out parametric symbols, such as pin locations,
     * sections and section positions, element size, etc.
     */
    static getSymbolInfo(element, renderedSymbolBounds = {}) {
        if (parametricInfoByPartUidCache && parametricInfoByPartUidCache[element.part_uid]) {
            // TODO this cache is indexed by part, not element, which means we don't respect
            // instance-specific properties when using this cache (currently just during drag).
            // We should fix this by including element properties in the cache key, but with
            // care not to regress perf in cases with thousands of instances of one part
            // (for eg a part document with thousands of terminals -- should be safe because
            // terminals are not parametric symbols, but proceed with caution) or
            // perhaps cases with many properties where the cache key may be expensive.
            return parametricInfoByPartUidCache[element.part_uid];
        }
        const elementSymbolStyle = ElementHelper.getSymbolStyle(element);
        const terminals = ElementHelper.getSortedTerminals(element, true);
        // Important to preserve order of symbols/sections/groups; therefore don't use lodash.groupBy here.
        // Using `||` not `??` is important below because we want to coerce both `undefined` and "" to
        // DEFAULT_SYMBOL_NAME (both for human sanity, and because Firestore doesn't allow empty keys).
        const pinsBySymbol = groupByWithPreservedOrder(terminals, (t) => (getPropertyValue(defaultPropertyDefinitions.subsymbol_designator_suffix.label, t.properties ?? {}) ||
            DEFAULT_SYMBOL_NAME));
        if (!pinsBySymbol.size) {
            // If there are no terminals, we still need to return a sane symbol. Used for components
            // that provide eg physical PCB objects like mount points.
            pinsBySymbol.set(DEFAULT_SYMBOL_NAME, []);
        }
        // If "Symbol Size" property is defined, scale the symbol to fit (currently a demo-only feature! Do not publicize.)
        // TODO IS NOT MEANINGFUL FOR MULTIPLE SYMBOLS!!
        const symbolSizeProp = getPropertyValue(defaultPropertyDefinitions.symbol_size.label, element.properties);
        const symbolSizeOverride = (() => {
            if (!symbolSizeProp)
                return undefined;
            const vector2Result = VectorObjectValue2.safeParse(symbolSizeProp);
            if (vector2Result.success) {
                return vector2Result.data.data;
            }
            const vector3Result = VectorObjectValue3.safeParse(symbolSizeProp);
            if (vector3Result.success) {
                return vector3Result.data.data;
            }
            return undefined;
        })();
        const symbolAssets = Object.values(element.part_version_data_cache.assets ?? {}).filter((a) => ["svg", "glb"].includes(SymbolLayout.getFileExtension(a.storageName)));
        const defaultSymbolAsset = symbolAssets.find((a) => a.isDefault);
        const assetsBySymbol = Object.fromEntries(symbolAssets
            .filter((a) => !!a.associatedSymbol)
            .map((a) => [a.associatedSymbol, a]));
        if (defaultSymbolAsset) {
            assetsBySymbol[DEFAULT_SYMBOL_NAME] = defaultSymbolAsset;
        }
        // Check for standard electrical symbol if no user symbol exists
        const standardSymbol = this.getStandardSymbol(element);
        // Track whether we injected the standard symbol as a fallback (vs user having their own asset)
        // Only use hardcoded pin positions from STANDARD_SYMBOL_PIN_MAPPING when we injected the fallback
        let didInjectStandardSymbol = false;
        if (!(DEFAULT_SYMBOL_NAME in assetsBySymbol) && !element.part_version_data_cache.symbol_resource_file) {
            if (standardSymbol) {
                assetsBySymbol[DEFAULT_SYMBOL_NAME] = { storageName: standardSymbol };
                didInjectStandardSymbol = true;
            }
        }
        if (!(DEFAULT_SYMBOL_NAME in assetsBySymbol) && element.part_version_data_cache.symbol_resource_file) {
            // Some symbols (eg genesis terminal) reference an asset that is not in the part's .assets list.
            // This is rare, and possibly limited to the genesis terminal, but we need the reference to work
            // at least for terminals. Treat it as a standard asset here.
            assetsBySymbol[DEFAULT_SYMBOL_NAME] = { storageName: element.part_version_data_cache.symbol_resource_file };
        }
        // Check if the user explicitly set the Symbol Style property on this element.
        // When set, the user's choice takes priority over any asset-based (SVG) layout.
        const symbolStylePropertyValue = getPropertyValue(defaultPropertyDefinitions.symbol_style.label, element.properties);
        const userOverridesSymbolStyle = symbolStylePropertyValue === "Parametric" || symbolStylePropertyValue === "Legacy";
        const symbols = [];
        for (const [symbolName, symbolPins] of pinsBySymbol.entries()) {
            const asset = assetsBySymbol[symbolName];
            // Only use standard symbol layout (with hardcoded pin positions) when:
            // 1. We injected the standard symbol as a fallback (user didn't have their own asset)
            // 2. This is the default symbol
            // 3. The asset matches the standard symbol path
            // If user has their own asset (even if same SVG), use CustomSymbolLayout to respect their terminal positions
            const useStandardLayout = didInjectStandardSymbol &&
                symbolName === DEFAULT_SYMBOL_NAME &&
                !!standardSymbol &&
                asset?.storageName === standardSymbol;
            if (asset && !userOverridesSymbolStyle) {
                if (useStandardLayout) {
                    // Use standard electrical symbol layout with hardcoded pin positions
                    symbols.push(this.calculateStandardSymbolLayout(symbolName, symbolPins, element, standardSymbol));
                }
                else {
                    symbols.push(CustomSymbolLayout.calculateSingleSymbolLayout(symbolName, symbolPins, asset, renderedSymbolBounds[symbolName]));
                }
            }
            else if (elementSymbolStyle === "legacy") {
                symbols.push(LegacySymbolLayout.calculateSingleSymbolLayout(symbolName, symbolPins));
            }
            else {
                symbols.push(this.calculateSingleSymbolLayout(symbolName, symbolPins, symbolSizeOverride));
            }
        }
        return symbols;
    }
    /**
     * Checks if a part's terminals can be properly matched to a standard symbol's pin mapping.
     * This uses the same matching logic as calculateStandardSymbolLayout to ensure consistency
     * between the frontend rendering and backend ranking.
     *
     * @param partType - The part type (e.g., "Resistor", "Capacitor")
     * @param terminals - Array of terminal objects with name and optional pinNumber
     * @returns true if all terminals can be matched to standard symbol pins
     */
    static canMatchStandardSymbolPins(partType, terminals) {
        if (!partType)
            return false;
        const symbolMapping = STANDARD_SYMBOL_PIN_MAPPING[partType];
        if (!symbolMapping)
            return false;
        // Check if terminal count matches
        if (terminals.length !== symbolMapping.pins.length) {
            return false;
        }
        const usedPins = new Set();
        // Try to match each terminal to a standard symbol pin using the same strategies
        // as calculateStandardSymbolLayout
        for (const terminal of terminals) {
            let mappedPin;
            // Strategy 1: Exact pin number match
            if (terminal.pinNumber) {
                mappedPin = symbolMapping.pins.find((p) => p.terminalNumber === terminal.pinNumber && !usedPins.has(p.terminalNumber));
            }
            // Strategy 2: Fuzzy name matching if no exact number match
            if (!mappedPin && terminal.name) {
                mappedPin = this.findFuzzyTerminalMatch(terminal.name, symbolMapping.pins, usedPins);
            }
            // Strategy 3: Sequential mapping for simple components
            if (!mappedPin && symbolMapping.pins.length === terminals.length) {
                const terminalIndex = terminals.indexOf(terminal);
                const availablePin = symbolMapping.pins[terminalIndex];
                if (availablePin && !usedPins.has(availablePin.terminalNumber)) {
                    mappedPin = availablePin;
                }
            }
            if (mappedPin) {
                usedPins.add(mappedPin.terminalNumber);
            }
            else {
                return false;
            }
        }
        return true;
    }
    /**
     * Get the standard electrical symbol file path for an element based on its part type.
     * Returns undefined if no standard symbol exists for the part type.
     */
    static getStandardSymbol(element) {
        const partType = getPropertyValue(defaultPropertyDefinitions.part_type.label, element.properties);
        if (typeof partType !== "string") {
            return undefined;
        }
        return STANDARD_SYMBOL_MAPPING[partType];
    }
    /**
     * Calculate symbol layout for standard electrical symbols with fuzzy terminal matching.
     * Maps user-created terminals to the standard symbol's predefined pin positions.
     */
    static calculateStandardSymbolLayout(symbolName, pins, element, standardSymbolPath) {
        const partType = getPropertyValue(defaultPropertyDefinitions.part_type.label, element.properties);
        const symbolMapping = typeof partType === "string" ? STANDARD_SYMBOL_PIN_MAPPING[partType] : undefined;
        if (!symbolMapping) {
            return this.calculateSingleSymbolLayout(symbolName, pins);
        }
        const pinLocations = {};
        const usedSymbolPins = new Set();
        const pinLength = 16;
        for (const pin of pins) {
            const pinNumber = getPropertyValue(defaultPropertyDefinitions.pin_number.label, pin.properties ?? {});
            const pinName = pin.name?.trim();
            let mappedPin;
            // Strategy 1: Exact pin number match
            mappedPin = symbolMapping.pins.find((p) => p.terminalNumber === String(pinNumber) && !usedSymbolPins.has(p.terminalNumber));
            // Strategy 2: Fuzzy name matching if no exact number match
            if (!mappedPin && pinName) {
                mappedPin = this.findFuzzyTerminalMatch(pinName, symbolMapping.pins, usedSymbolPins);
            }
            // Strategy 3: Sequential mapping for simple components
            if (!mappedPin && symbolMapping.pins.length === pins.length) {
                const terminalIndex = pins.indexOf(pin);
                const availablePin = symbolMapping.pins[terminalIndex];
                if (availablePin && !usedSymbolPins.has(availablePin.terminalNumber)) {
                    mappedPin = availablePin;
                }
            }
            // Strategy 4: Use existing symbol_pin_position
            if (!mappedPin) {
                const existingPosition = getPropertyValue(defaultPropertyDefinitions.symbol_pin_position.label, pin.properties ?? {});
                if (existingPosition && typeof existingPosition === "object" && "data" in existingPosition) {
                    const posData = existingPosition.data;
                    if (posData && typeof posData === "object" && "x" in posData && "y" in posData) {
                        pinLocations[pin.uid] = {
                            position: posData,
                            direction: this.inferDirectionFromPosition(posData),
                        };
                        continue;
                    }
                }
            }
            if (mappedPin) {
                pinLocations[pin.uid] = {
                    position: mappedPin.position,
                    direction: this.inferDirectionFromPosition(mappedPin.position),
                };
                usedSymbolPins.add(mappedPin.terminalNumber);
            }
        }
        // Calculate bounds from terminal positions, then add padding for the symbol body.
        // Standard electrical symbol SVGs extend beyond the terminal connection points,
        // so we need to add padding to account for the actual symbol size.
        const terminalPositions = Object.values(pinLocations).map((loc) => loc.position);
        let outerBounds = CustomSymbolLayout.estimateSymbolBounds(terminalPositions);
        // Add padding to account for the symbol body extending beyond terminal positions.
        // Standard symbols typically have a body that extends ~24 units beyond each terminal.
        const symbolPadding = 24;
        outerBounds = {
            min: { x: outerBounds.min.x - symbolPadding, y: outerBounds.min.y - symbolPadding },
            max: { x: outerBounds.max.x + symbolPadding, y: outerBounds.max.y + symbolPadding },
        };
        return {
            name: symbolName,
            outlineSize: box2.size(outerBounds),
            outerBounds,
            pinLength: 0, // Standard electrical symbols include their pins as part of the SVG
            pinLocations,
            pins,
            sections: [],
            symbolStyle: "standard",
            assetFilename: standardSymbolPath,
        };
    }
    /**
     * Find a fuzzy match for a terminal name against standard electrical symbol pin aliases.
     */
    static findFuzzyTerminalMatch(terminalName, symbolPins, usedPins) {
        const normalizedName = terminalName.toLowerCase().trim();
        // Try exact alias match first
        let match = symbolPins.find((p) => p.aliases.some((alias) => alias.toLowerCase() === normalizedName) && !usedPins.has(p.terminalNumber));
        if (match)
            return match;
        // Try partial matches
        match = symbolPins.find((p) => p.aliases.some((alias) => alias.toLowerCase().includes(normalizedName) || normalizedName.includes(alias.toLowerCase())) && !usedPins.has(p.terminalNumber));
        if (match)
            return match;
        // Try common terminal name patterns
        const commonPatterns = {
            vcc: ["vcc", "vdd", "power", "v+"],
            gnd: ["gnd", "ground", "vss", "v-"],
            in: ["in", "input", "a"],
            out: ["out", "output", "y", "q"],
            clk: ["clk", "clock", "c"],
        };
        for (const [pattern, alternatives] of Object.entries(commonPatterns)) {
            if (alternatives.includes(normalizedName)) {
                match = symbolPins.find((p) => p.aliases.some((alias) => alias.toLowerCase() === pattern) && !usedPins.has(p.terminalNumber));
                if (match)
                    return match;
            }
        }
        return undefined;
    }
    /**
     * Infer pin direction from its position relative to the symbol center.
     */
    static inferDirectionFromPosition(position) {
        if (Math.abs(position.x) > Math.abs(position.y)) {
            return position.x < 0 ? "left" : "right";
        }
        return position.y < 0 ? "down" : "up";
    }
    static calculateSingleSymbolLayout(symbolName, pins, symbolSizeOverride) {
        let sections = [];
        let sectionVerticalPos = 0;
        const pinLocations = {};
        const pinGridSize = SymbolConstants.pinGridSize;
        let pinLength = pinGridSize * 2;
        const pinsBySection = groupByWithPreservedOrder(pins, (t) => (getPropertyValue(defaultPropertyDefinitions.section.label, t.properties ?? {}) ?? ""));
        if (!pinsBySection.size) {
            pinsBySection.set("", []);
        }
        for (const [section, sectionPins] of pinsBySection.entries()) {
            const sectionInfo = this.calculateSectionLayout(sectionPins);
            Object.assign(pinLocations, mapValues(sectionInfo.pinLocations, (l) => ({
                ...l,
                position: vec2.add(l.position, { x: 0, y: -sectionVerticalPos }),
            })));
            sections.push({ name: section, position: -sectionVerticalPos, height: sectionInfo.height });
            sectionVerticalPos += sectionInfo.height;
        }
        let outlineSize = { x: SymbolConstants.moduleOutlineWidth, y: sectionVerticalPos };
        if (symbolSizeOverride) {
            pinLength =
                symbolSizeOverride.x >= SymbolConstants.moduleOutlineWidth + pinLength * 2
                    ? pinGridSize * 2
                    : pinGridSize;
            const heightRatio = symbolSizeOverride.y / outlineSize.y;
            outlineSize = vec2.sub(symbolSizeOverride, { x: pinLength * 2, y: 0 });
            // Snap pins to either 1x or 2x pinGridSize, unless symbol size is too small for
            // even 1x, in which case put pins as close together as necessary.
            const gridRatio = heightRatio < 0.5 ? heightRatio : heightRatio < 1 ? 0.5 : 1;
            for (const location of Object.values(pinLocations)) {
                location.position.y *= gridRatio;
                const sign = location.position.x >= 0 ? 1 : -1;
                location.position.x = (symbolSizeOverride.x / 2) * sign;
            }
            for (const section of sections) {
                section.position *= gridRatio;
                section.height *= gridRatio;
            }
            const lastSection = sections.at(-1);
            if (lastSection) {
                lastSection.height = outlineSize.y - -lastSection.position;
            }
        }
        // Symbol origin is at center, so offset y accordingly:
        for (const location of Object.values(pinLocations)) {
            location.position.y += outlineSize.y / 2;
        }
        sections.sort((a, b) => b.position - a.position);
        sections = sections.map((s) => ({ ...s, position: s.position + outlineSize.y / 2 }));
        // Note, when we add top/bottom pins, we'll want to expand the height conditionally to
        // accommodate those pins too if they exist.
        const symbolSize = { x: outlineSize.x + 2 * pinLength, y: outlineSize.y };
        const outerBounds = {
            min: { x: -symbolSize.x / 2, y: -symbolSize.y / 2 },
            max: { x: +symbolSize.x / 2, y: +symbolSize.y / 2 },
        };
        return {
            name: symbolName,
            outlineSize,
            outerBounds,
            pinLocations,
            pinLength,
            pins,
            sections,
            symbolStyle: "parametric-v0",
        };
    }
    static calculateSectionLayout(pins) {
        function getPinOrientation(pin) {
            const rawValue = getPropertyValue(defaultPropertyDefinitions.pin_orientation.label, pin.properties ?? {});
            const validDirections = ["up", "down", "left", "right"];
            // Don't fail hard if part designer provides an invalid value for pin orientation;
            // just treat it as undefined, so the part can still be used.
            const value = rawValue ? rawValue.toLowerCase() : undefined;
            return value && validDirections.includes(value) ? value : undefined;
        }
        const pinGridSize = SymbolConstants.pinGridSize;
        const pinLength = pinGridSize * 2;
        // Use `t.uid`, not `""`, as fallback below, so as to treat pins with no assigned pin group
        // as having an individual pin group each (per design decision 9 Feb)
        const pinsByGroup = groupByWithPreservedOrder(pins, 
        // `||` not `??` is important below, to treat both "" and undefined as groupless.
        // Per 30 Aug 2024 discussion:
        // [workplace-post/459034239506853](/docs/workplace/1252696749407743.md#1253097202701031)
        (t) => (getPropertyValue(defaultPropertyDefinitions.pin_group.label, t.properties ?? {}) || t.uid));
        const pinGroupsWithInfo = [...pinsByGroup.entries()].map(([pinGroup, pins]) => {
            // Orientation of group is the orientation of the first pin with a defined
            // orientation. If none, then it's automatic.
            return {
                pinGroup,
                pins,
                height: pins.length + 1,
                direction: pins.map((p) => getPinOrientation(p)).find((d) => !!d),
            };
        });
        // Group pin groups into left and right side using Greedy Number Partitioning /
        // LPT-scheduling: https://en.wikipedia.org/wiki/Greedy_number_partitioning
        // Sort in decreasing pin-group height, because that results in better allocation than arbitrary order:
        pinGroupsWithInfo.sort(keySort(({ height }) => -height));
        const edgeLengths = { left: 0, right: 0, up: 0, down: 0 };
        const edgePinGroups = { left: [], right: [], up: [], down: [] };
        // First add all pins with a defined direction:
        for (const pinGroupInfo of pinGroupsWithInfo) {
            if (!pinGroupInfo.direction)
                continue;
            edgeLengths[pinGroupInfo.direction] += pinGroupInfo.height;
            edgePinGroups[pinGroupInfo.direction].push(pinGroupInfo);
        }
        // Then add all pins with no defined direction:
        for (const pinGroupInfo of pinGroupsWithInfo) {
            if (pinGroupInfo.direction)
                continue;
            const side = edgeLengths.left <= edgeLengths.right ? "left" : "right";
            edgeLengths[side] += pinGroupInfo.height;
            edgePinGroups[side].push(pinGroupInfo);
        }
        const pinLocations = {};
        for (const side of ["left", "right"]) {
            let pinVerticalOffset = pinGridSize;
            const sign = side === "left" ? -1 : 1;
            for (const pinGroupInfo of edgePinGroups[side]) {
                pinVerticalOffset += pinGridSize;
                for (const pin of pinGroupInfo.pins) {
                    pinLocations[pin.uid] = {
                        position: {
                            x: (SymbolConstants.moduleOutlineWidth / 2 + pinLength) * sign,
                            y: -pinVerticalOffset,
                        },
                        direction: side,
                    };
                    pinVerticalOffset += pinGridSize;
                }
            }
        }
        const height = Math.max(edgeLengths.left, edgeLengths.right) * pinGridSize + pinGridSize * 2;
        return { pinLocations, height };
    }
    static getFileExtension(fileName) {
        return fileName.split(".").pop()?.toLowerCase() || "";
    }
}
class LegacySymbolLayout {
    static calculateSingleSymbolLayout(symbolName, pins) {
        const numPins = pins.length;
        const pinLocations = Object.fromEntries(pins.map((t, idx) => [t.uid, this.getPinInfo(idx, numPins)]));
        const outlineSize = this.getOutlineSize(numPins);
        const pinLength = SymbolConstants.legacyPinLength;
        const symbolSize = vec2.add(outlineSize, { x: pinLength * 2, y: 0 });
        const outerBounds = {
            min: { x: -symbolSize.x / 2, y: -symbolSize.y / 2 },
            max: { x: +symbolSize.x / 2, y: +symbolSize.y / 2 },
        };
        return {
            name: symbolName,
            outlineSize,
            outerBounds,
            pinLocations,
            pinLength,
            pins,
            symbolStyle: "legacy",
            sections: [],
        };
    }
    /** Number of left-side pins on an auto-symbol module. Super simple calc, but in a function for consistency. */
    static numLeftSidePins(numPins) {
        return Math.ceil(numPins / 2);
    }
    static getOutlineSize(numPins) {
        const leftSidePins = this.numLeftSidePins(numPins);
        const width = SymbolConstants.min_object_width;
        let height = (leftSidePins - 1) * SymbolConstants.pin_to_pin_margin +
            (SymbolConstants.pin_margin_top + SymbolConstants.pin_margin_bottom);
        height = Math.max(height, SymbolConstants.min_object_height);
        return { x: width, y: height };
    }
    static getPinInfo(index, numPins) {
        const leftSidePins = this.numLeftSidePins(numPins);
        const size = this.getOutlineSize(numPins);
        const leftSide = index < leftSidePins;
        return {
            position: {
                x: (size.x / 2 + SymbolConstants.legacyPinLength) * (leftSide ? -1 : 1),
                y: size.y / 2 -
                    (SymbolConstants.pin_margin_top + (index % leftSidePins) * SymbolConstants.pin_to_pin_margin),
            },
            direction: leftSide ? "left" : "right",
        };
    }
}
//# sourceMappingURL=SymbolLayout.js.map